// Pull a claimed person's cards and collection onto this device.
//
// Runs after an OTP claim, and on load whenever a session exists. It is a
// one-way merge: the server is authoritative for a card's CONTENT, but the
// device keeps anything the server does not know about, so a draft made offline
// on this phone is never silently destroyed by signing in.
//
// It never changes a card's public id. That id is written onto physical NFC
// tags (constraint C-2, ~30 bytes on an NTAG213), so a card that came back from
// the server keeps the exact id its tag already points at.
import { fetchSync, type SyncCard } from './api';
import { publicCard, type Card } from './pocketbase';
import { listMyCards, saveMyCard, setActiveCard, activeCardId } from './mycard';

/** Map a synced card to the local shape. `editToken` is deliberately absent:
 *  it is only ever issued once, to the publishing device. A synced device edits
 *  through its session instead — the server accepts either. */
function toCard(s: SyncCard, previous?: Partial<Card> | null): Card {
  return {
    id: s.publicId,
    publicId: s.publicId,
    // Keep a token this device already holds; sync never supplies one.
    editToken: previous?.publicId === s.publicId ? previous?.editToken : undefined,
    name: s.name,
    title: s.title ?? '',
    department: s.department ?? '',
    company: s.company ?? '',
    monogram: (s.name || '?').trim().charAt(0).toUpperCase() || '?',
    phone: s.phone ?? '',
    email: s.email ?? '',
    website: s.socials?.website ?? '',
    address: s.address ?? '',
    industry: s.industry ?? '',
    extraPhones: s.extraPhones,
    extraEmails: s.extraEmails,
    theme: (s.theme as Card['theme']) || 'graphite',
    styling: s.styling,
    photo: s.photo ?? '',
    backImage: s.backImage ?? '',
    extras: s.extras,
  };
}

/** What a sync changed, for the caller to report or ignore. */
export interface SyncSummary { card: boolean; collected: number }

/**
 * Bring this device in line with the account.
 *
 * Collected cards are fetched through the normal resolve route, which caches
 * each one into IndexedDB — so the directory keeps working offline afterwards,
 * exactly as it does for a card collected by tapping.
 */
export async function syncFromAccount(): Promise<SyncSummary> {
  const result = await fetchSync();
  const summary: SyncSummary = { card: false, collected: 0 };

  // Every card the account owns, not just the first — a person may hold a work
  // card and a personal one, each with its own tag.
  const existing = await listMyCards<Partial<Card>>();
  const byId = new Map(existing.map((c) => [c.publicId, c]));
  const wasActive = await activeCardId();

  for (const card of result.cards) {
    await saveMyCard(toCard(card, byId.get(card.publicId)));
    summary.card = true;
  }
  // saveMyCard makes each saved card active, so restore the one that was —
  // syncing should not silently switch which card someone is looking at.
  if (wasActive) await setActiveCard(wasActive);

  // Resolve sequentially rather than in parallel: this runs right after a claim,
  // often on a phone, and the resolve route is per-IP rate limited — a burst of
  // twenty would throttle itself.
  for (const id of result.collected) {
    if (await publicCard(id)) summary.collected++;
  }

  return summary;
}
